import { useMemo, useRef } from 'react';

import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';
import {
	createPageEditorPluginAIOptions,
	createPageCommentEditorPluginAIOptions,
} from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { AIProactivePluginConfig } from '@atlassian/editor-plugin-ai-proactive';
import { useSourceAIActions } from '@atlassian/ai-smart-button-api';

import { fg } from '@confluence/feature-gating';
import { useSpaceId } from '@confluence/space-utils';
import { useSessionData } from '@confluence/session-data';

import { useLinkingFeatures } from '../components/EditorLinkingFeatures';

import { useMentionProvider } from './useMentionProvider';
import { useEditorPluginAIProactiveEnabled } from './useEditorPluginAIProactive';

export type Sentiment = 'good' | 'bad' | 'unknown';

type EditorPluginAIOptions = Parameters<typeof createPageEditorPluginAIOptions>[0];
type HandleFeedbackSubmission = NonNullable<EditorPluginAIOptions>['handleFeedbackSubmission'];
type HandleFeedbackSubmissionAIProactive = AIProactivePluginConfig['handleFeedbackSubmission'];

export type HandleFeedbackSubmissionArgs =
	| Parameters<NonNullable<HandleFeedbackSubmission>>[0]
	| Parameters<NonNullable<HandleFeedbackSubmissionAIProactive>>[0];

export const EDITOR_AI_ACTION_SOURCE_ID = 'editor';

interface ConfluenceEditorPluginAIOptions {
	appearance?: 'full-page' | 'page-comment';
	/**
	 * Triggers when "thumbs up/down" feedback buttons are is clicked - probably
	 * want to start a survey (freeform field) at this point to collect any
	 * further details.
	 *
	 * Sentiment shape/details may change in future, we may expose that information
	 * more directly as explicit data shapes, but abstracting away to events
	 * for now to get an early iteration working.
	 */

	onFeedbackClicked?: (props: HandleFeedbackSubmissionArgs) => void;
	/**
	 * Triggers when the AI plugin enters a review state (response finished streaming)
	 */
	onReviewStateEntered?: () => void;
	/**
	 * Triggers when the AI plugin experience is complete, e.g. user has inserted
	 * content into the page, ending the AI experience.
	 */
	onExperienceComplete?: () => void;
	/**
	 * Opt in status of AI
	 */
	aiGlobalOptIn: EditorPluginAIOptions['aiGlobalOptIn'];

	AIButtonWrapper?: React.FC<{ children: React.ReactNode }>;
	/**
	 * Used to generate hash for pages channelID with use of Rovo agents.
	 */
	getChannelVariables?: EditorPluginAIOptions['getChannelVariables'];

	/**
	 * Triggered when the document has been changed by a Rovo agent.
	 */
	onDocChangeByAgent?: EditorPluginAIOptions['onDocChangeByAgent'];

	/**
	 * Triggered when the AI provider interaction starts.
	 */
	onAIProviderChanged?: EditorPluginAIOptions['onAIProviderChanged'];

	providers?: EditorPluginAIOptions['providers'];

	/**
	 * contentId (aka pageId or objectId) to use in audit log settings.
	 */
	contentId?: string;
}

const CARD_OPTIONS = { allowDatasource: true };

const defaultOptions: ConfluenceEditorPluginAIOptions = {
	appearance: 'full-page',
	onFeedbackClicked: () => {},
	onReviewStateEntered: () => {},
	onExperienceComplete: () => {},
	aiGlobalOptIn: { status: 'disabled', triggerOptInFlow: () => {} },
};

export const useEditorPluginAIOptions = (
	options?: ConfluenceEditorPluginAIOptions,
): ReturnType<typeof createPageEditorPluginAIOptions> => {
	const optionsToUse = options || defaultOptions;

	const createEditorPluginAIOptions = useMemo(
		() =>
			optionsToUse.appearance === 'page-comment'
				? createPageCommentEditorPluginAIOptions
				: createPageEditorPluginAIOptions,
		[optionsToUse.appearance],
	);

	const optionsToUseRef = useRef(optionsToUse);
	optionsToUseRef.current = optionsToUse;

	const { cloudId } = useSessionData();
	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);
	const linkPicker = useLinkPickerEditorProps(undefined, productConfig);
	const spaceId = useSpaceId();
	const mentionProvider = useMentionProvider(spaceId);

	const linkingProp = useLinkingFeatures(CARD_OPTIONS);

	const PromptEditor = useMemo(
		() =>
			createPromptEditor({
				linkPicker,
				enableLinks: true,
				linking: linkingProp.linking,
				mentionProvider,
				featureFlags: {},
			}),
		[linkingProp.linking, linkPicker, mentionProvider],
	);

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: optionsToUse.aiGlobalOptIn.status,
	});

	const aiSmartButtonActions = useSourceAIActions(EDITOR_AI_ACTION_SOURCE_ID);

	const isAISmartButtonEnabled = useMemo(
		() =>
			optionsToUse.appearance !== 'page-comment' &&
			optionsToUse.aiGlobalOptIn.status === 'enabled' &&
			fg('ai-smart-button-team-25'),
		[optionsToUse.appearance, optionsToUse.aiGlobalOptIn.status],
	);

	const aiSmartButtonOptions = useMemo(
		() =>
			isAISmartButtonEnabled
				? {
						actions: aiSmartButtonActions,
					}
				: undefined,
		[isAISmartButtonEnabled, aiSmartButtonActions],
	);
	return useMemo(() => {
		return createEditorPluginAIOptions({
			aiGlobalOptIn: optionsToUseRef.current.aiGlobalOptIn,
			/**
			 * Hide AI button in primary toolbar when AI proactive is enabled or
			 * 	when appearance is full-page and AI smart button is enabled
			 */
			hidePrimaryToolbarButton:
				editorPluginAIProactiveEnabled ||
				(optionsToUse.appearance === 'full-page' &&
					optionsToUse.aiGlobalOptIn.status === 'enabled' &&
					fg('ai-smart-button-team-25')),
			generativeAIApiUrl: `${window.location.origin}/gateway/api/editor-ai/v1/generative/ai`,
			handleFeedbackSubmission: async (feedbackMetadata) => {
				try {
					// TODO: EDF-831: Fix and ensure types match with handleFeedbackSubmission
					optionsToUseRef.current.onFeedbackClicked?.(feedbackMetadata);
					return { status: 'submitted' as const };
				} catch (err) {
					return { status: 'failed' as const };
				}
			},
			onExperienceEvent: {
				// Thumbs up and down are being deprecated and are no longer used.
				THUMBS_UP: () => {},
				THUMBS_DOWN: () => {},
				REVIEW_STATE_ENTERED: () => {
					optionsToUseRef.current.onReviewStateEntered?.();
				},
				EXPERIENCE_COMPLETE: () => {
					optionsToUseRef.current.onExperienceComplete?.();
				},
			},
			onDocChangeByAgent: (_agent) => {
				// TODO Integrate with confluence
			},
			onAIProviderChanged: (source, aiProvider) => {
				if (optionsToUseRef.current.onAIProviderChanged) {
					optionsToUseRef.current.onAIProviderChanged(source, aiProvider);
				}
			},
			getChannelVariables: optionsToUseRef.current.getChannelVariables,
			product: 'CONFLUENCE',
			objectId: optionsToUseRef.current.contentId,
			PromptEditor,
			AIButtonWrapper: options?.AIButtonWrapper,
			providers: options?.providers,
			/**
			 * Need to audit logs only for confluence page as of now.
			 */
			auditLogSettings:
				optionsToUse.appearance !== 'page-comment' && optionsToUse.contentId
					? {
							enabled: true,
							eventData: {
								type: 'page',
								pageId: optionsToUse.contentId,
							},
						}
					: { enabled: false },
			...(isAISmartButtonEnabled && {
				aiSmartButtonOptions,
			}),
		});
	}, [
		createEditorPluginAIOptions,
		editorPluginAIProactiveEnabled,
		optionsToUse.appearance,
		optionsToUse.contentId,
		PromptEditor,
		options?.AIButtonWrapper,
		options?.providers,
		isAISmartButtonEnabled,
		aiSmartButtonOptions,
		optionsToUse.aiGlobalOptIn.status,
	]);
};
